import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Profile, ProfileShare } from '../types';
import { profileService } from '../services/dataService';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber, InputNumberValueChangeEvent } from 'primereact/inputnumber';
import { Dialog } from 'primereact/dialog';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { Chip } from 'primereact/chip';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

interface FormData {
  name: string;
  max_review_days: number;
}

interface PermissionOption {
  label: string;
  value: 'read' | 'edit';
}

function Profiles(): React.ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [shareDialogVisible, setShareDialogVisible] = useState(false);
  const [editingProfile, setEditingProfile] = useState<Profile | null>(null);
  const [formData, setFormData] = useState<FormData>({ name: '', max_review_days: 7 });
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'read' | 'edit'>('read');

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async (): Promise<void> => {
    try {
      const response = await profileService.getAll();
      if (response.data.success) {
        setProfiles(response.data.profiles || []);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    try {
      if (editingProfile) {
        // Update profile
        await profileService.update(editingProfile.id, formData);
      } else {
        // Create profile
        await profileService.create(formData);
      }
      await loadProfiles();
      setDialogVisible(false);
      resetForm();
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  };

  const handleDelete = (profile: Profile): void => {
    confirmDialog({
      message: t('common.confirm') + '?',
      header: t('profile.delete'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await profileService.delete(profile.id);
          await loadProfiles();
        } catch (error) {
          console.error('Error deleting profile:', error);
        }
      }
    });
  };

  const handleShare = async (): Promise<void> => {
    try {
      if (editingProfile) {
        await profileService.share(editingProfile.id, shareEmail, sharePermission);
        await loadProfiles();
      }
      setShareDialogVisible(false);
      setShareEmail('');
    } catch (error) {
      console.error('Error sharing profile:', error);
    }
  };

  const resetForm = (): void => {
    setFormData({ name: '', max_review_days: 7 });
    setEditingProfile(null);
  };

  const openEditDialog = (profile: Profile): void => {
    setEditingProfile(profile);
    setFormData({ name: profile.name, max_review_days: profile.max_review_days });
    setDialogVisible(true);
  };

  const openShareDialog = (profile: Profile): void => {
    setEditingProfile(profile);
    setShareDialogVisible(true);
  };

  const permissionOptions: PermissionOption[] = [
    { label: t('profile.readOnly'), value: 'read' },
    { label: t('profile.canEdit'), value: 'edit' }
  ];

  if (loading) {
    return (
      <div className="flex align-items-center justify-content-center" style={{ height: '50vh' }}>
        <i className="pi pi-spin pi-spinner text-4xl"></i>
      </div>
    );
  }

  return (
    <div>
      <ConfirmDialog />
      
      <div className="flex justify-content-between align-items-center mb-4">
        <h1 className="text-2xl font-bold m-0">{t('nav.profiles')}</h1>
        <Button 
          label={t('profile.create')} 
          icon="pi pi-plus"
          onClick={() => {
            resetForm();
            setDialogVisible(true);
          }}
        />
      </div>

      {profiles.length === 0 ? (
        <Card className="text-center p-5">
          <i className="pi pi-user text-5xl text-color-secondary mb-3"></i>
          <h3>{t('profile.create')}</h3>
          <Button 
            label={t('profile.create')} 
            icon="pi pi-plus"
            onClick={() => setDialogVisible(true)}
          />
        </Card>
      ) : (
        <div className="grid">
          {profiles.map(profile => (
            <div key={profile.id} className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <div className="flex justify-content-between align-items-start mb-3">
                  <div>
                    <h3 className="m-0 mb-1">{profile.name}</h3>
                    <div className="text-sm text-color-secondary">
                      {profile.memorized_pages} {t('dashboard.pagesMemorized')}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      icon="pi pi-share-alt" 
                      className="p-button-rounded p-button-text p-button-sm"
                      onClick={() => openShareDialog(profile)}
                      tooltip={t('profile.shareProfile')}
                    />
                    <Button 
                      icon="pi pi-pencil" 
                      className="p-button-rounded p-button-text p-button-sm"
                      onClick={() => openEditDialog(profile)}
                    />
                    <Button 
                      icon="pi pi-trash" 
                      className="p-button-rounded p-button-text p-button-danger p-button-sm"
                      onClick={() => handleDelete(profile)}
                    />
                  </div>
                </div>

                <div className="text-sm mb-3">
                  <span className="text-color-secondary">{t('profile.maxReviewDays')}: </span>
                  <strong>{profile.max_review_days}</strong>
                </div>

                {profile.shared_with && profile.shared_with.length > 0 && (
                  <div className="mb-3">
                    <div className="text-sm text-color-secondary mb-2">{t('profile.sharedWith')}:</div>
                    <div className="flex flex-wrap gap-1">
                      {profile.shared_with.map((share: ProfileShare, idx: number) => (
                        <Chip 
                          key={idx} 
                          label={`${share.email} (${share.permission})`}
                          removable
                          onRemove={() => {
                            if (share.user_id) {
                              profileService.removeShare(profile.id, share.user_id)
                                .then(() => loadProfiles())
                                .catch((error) => console.error('Error removing share:', error));
                            }
                            return true;
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <Button 
                  label={t('nav.pages')} 
                  icon="pi pi-arrow-right"
                  iconPos="right"
                  className="w-full"
                  onClick={() => navigate(`/pages/${profile.id}`)}
                />
              </Card>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        header={editingProfile ? t('profile.edit') : t('profile.create')}
        visible={dialogVisible}
        onHide={() => {
          setDialogVisible(false);
          resetForm();
        }}
        style={{ width: '90vw', maxWidth: '400px' }}
      >
        <div className="field mb-3">
          <label htmlFor="name" className="block mb-2 font-medium">
            {t('profile.name')}
          </label>
          <InputText
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full"
          />
        </div>

        <div className="field mb-4">
          <label htmlFor="maxDays" className="block mb-2 font-medium">
            {t('profile.maxReviewDays')}
          </label>
          <InputNumber
            id="maxDays"
            value={formData.max_review_days}
            onValueChange={(e: InputNumberValueChangeEvent) => setFormData({ ...formData, max_review_days: e.value || 7 })}
            min={1}
            max={30}
            className="w-full"
          />
        </div>

        <div className="flex justify-content-end gap-2">
          <Button 
            label={t('common.cancel')} 
            className="p-button-text"
            onClick={() => {
              setDialogVisible(false);
              resetForm();
            }}
          />
          <Button 
            label={t('common.save')} 
            onClick={handleSave}
            disabled={!formData.name}
          />
        </div>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        header={t('profile.shareProfile')}
        visible={shareDialogVisible}
        onHide={() => {
          setShareDialogVisible(false);
          setShareEmail('');
        }}
        style={{ width: '90vw', maxWidth: '400px' }}
      >
        <div className="field mb-3">
          <label htmlFor="shareEmail" className="block mb-2 font-medium">
            {t('auth.email')}
          </label>
          <InputText
            id="shareEmail"
            type="email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            className="w-full"
            placeholder="email@example.com"
          />
        </div>

        <div className="field mb-4">
          <label className="block mb-2 font-medium">Permission</label>
          <Dropdown
            value={sharePermission}
            options={permissionOptions}
            onChange={(e: DropdownChangeEvent) => setSharePermission(e.value)}
            className="w-full"
          />
        </div>

        <div className="flex justify-content-end gap-2">
          <Button 
            label={t('common.cancel')} 
            className="p-button-text"
            onClick={() => {
              setShareDialogVisible(false);
              setShareEmail('');
            }}
          />
          <Button 
            label={t('profile.shareProfile')} 
            icon="pi pi-share-alt"
            onClick={handleShare}
            disabled={!shareEmail}
          />
        </div>
      </Dialog>
    </div>
  );
}

export default Profiles;
